import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  Link,
  Tabs,
  Tab,
  Paper,
  Divider,
  Alert,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { keyframes } from '@emotion/react';
import { useTheme } from '@mui/material/styles';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { KeyResultTemplatePanel, KeyResultCustomCatalogPanel } from './KeyResultTemplatePicker';
import {
  resolveOkrCategoryForTemplate,
  isCustomCatalogLine,
  findCatalogKeyResultForTemplateInLibraryFamily,
  pickCategoryForNewTemplateCatalogRow,
  catalogRowHasTargetForJobRole,
  targetRowJobRoleId,
  targetRowTitleId,
  setOkrTemplateLibrary
} from './okrKeyResultNameTemplates';

/** Blinking “point here” cue beside the full-matrix link */
const handPointPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(1.2); }
`;

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function intWhole(v) {
  const n = parseInt(String(v).trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function intPercent0to100(v) {
  const n = Math.round(parseFloat(String(v)));
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

const DEFAULT_KEY_RESULT_FREQ = {
  daily: true,
  weekly: true,
  monthly: true,
  quarterly: true,
  yearly: true
};

const LS_KR_ROW_MODE = 'erp4c.okr.krRowOpenMode';

/** D×5 weekly; ~22 workdays/month; ~3 months/quarter; ~250 workdays/year (adjust if needed). */
const FROM_DAILY_MULT = { weekly: 5, monthly: 22, quarterly: 66, yearly: 250 };

function normalizeKeyResultFreq(f) {
  if (!f || typeof f !== 'object') return { ...DEFAULT_KEY_RESULT_FREQ };
  const o = {
    daily: f.daily !== false,
    weekly: f.weekly !== false,
    monthly: f.monthly !== false,
    quarterly: f.quarterly !== false,
    yearly: f.yearly !== false
  };
  if (!o.daily && !o.weekly && !o.monthly && !o.quarterly && !o.yearly) o.daily = true;
  return o;
}

function applyFrequencyToLevelDrafts(levels, numberByLevel, periodByLevel, freq) {
  const f = normalizeKeyResultFreq(freq);
  const n = { ...numberByLevel };
  const p = { ...periodByLevel };
  for (const lvl of levels) {
    if (!f.daily) n[lvl.id] = '';
    const ed = p[lvl.id] ? { ...p[lvl.id] } : emptyPeriodDraft();
    if (!f.weekly) ed.weekly = '';
    if (!f.monthly) ed.monthly = '';
    if (!f.quarterly) ed.quarterly = '';
    if (!f.yearly) ed.yearly = '';
    p[lvl.id] = ed;
  }
  return { n, p };
}

function emptyPeriodDraft() {
  return { weekly: '', monthly: '', quarterly: '', yearly: '' };
}

function strFromTargetField(x, field) {
  const v = x[field];
  if (v == null || v === '') return '';
  const n = Math.round(parseFloat(v));
  return Number.isFinite(n) ? String(n) : '';
}

function periodDraftFromTargetRow(x) {
  return {
    weekly: strFromTargetField(x, 'target_number_weekly'),
    monthly: strFromTargetField(x, 'target_number_monthly'),
    quarterly: strFromTargetField(x, 'target_number_quarterly'),
    yearly: strFromTargetField(x, 'target_number_yearly')
  };
}

function periodPayloadFromDraft(d) {
  const z = d && typeof d === 'object' ? d : emptyPeriodDraft();
  return {
    target_number_weekly: intWhole(z.weekly),
    target_number_monthly: intWhole(z.monthly),
    target_number_quarterly: intWhole(z.quarterly),
    target_number_yearly: intWhole(z.yearly)
  };
}

function periodPayloadFromTargetRow(t) {
  return {
    target_number_weekly: intWhole(t.target_number_weekly),
    target_number_monthly: intWhole(t.target_number_monthly),
    target_number_quarterly: intWhole(t.target_number_quarterly),
    target_number_yearly: intWhole(t.target_number_yearly)
  };
}

/** Merge tab-scoped level edits with existing targets for other job roles (PUT replaces all targets). */
function mergeRoleTargetsForTabSave(
  kr,
  activeRoleId,
  levelsForRole,
  editedWeightByLevel,
  editedNumberByLevel,
  editedPeriodByLevel = {}
) {
  const out = [];
  const ar = String(activeRoleId);
  for (const t of kr.targets || []) {
    if (String(targetRowJobRoleId(t) ?? '') === ar) continue;
    out.push({
      employee_job_role_id: targetRowJobRoleId(t) ?? t.employee_job_role_id,
      employee_title_id: targetRowTitleId(t) ?? t.employee_title_id,
      target_percent: intPercent0to100(t.target_percent),
      target_number: intWhole(t.target_number),
      ...periodPayloadFromTargetRow(t)
    });
  }
  for (const lvl of levelsForRole) {
    out.push({
      employee_job_role_id: activeRoleId,
      employee_title_id: lvl.id,
      target_percent: intPercent0to100(editedWeightByLevel?.[lvl.id]),
      target_number: intWhole(editedNumberByLevel?.[lvl.id]),
      ...periodPayloadFromDraft(editedPeriodByLevel?.[lvl.id])
    });
  }
  return out;
}

function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return { r: 99, g: 102, b: 241 };
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return { r: 99, g: 102, b: 241 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Full-row tint from category color (stronger on hover). */
function categoryRowBg(hex, hover) {
  const { r, g, b } = hexToRgb(hex);
  const a = hover ? 0.18 : 0.11;
  return `rgba(${r},${g},${b},${a})`;
}

function categoryNameOf(kr) {
  const c = kr.category || {};
  return String(c.name || '');
}

function pctForTargetLevel(kr, roleId, titleId) {
  const t = (kr.targets || []).find(
    (x) =>
      String(targetRowJobRoleId(x) ?? '') === String(roleId) &&
      String(targetRowTitleId(x) ?? '') === String(titleId)
  );
  return t != null ? intPercent0to100(t.target_percent) : null;
}

function numForTargetLevel(kr, roleId, titleId) {
  const t = (kr.targets || []).find(
    (x) =>
      String(targetRowJobRoleId(x) ?? '') === String(roleId) &&
      String(targetRowTitleId(x) ?? '') === String(titleId)
  );
  return t != null ? intWhole(t.target_number) : null;
}

/** @param {'target_number_weekly'|'target_number_monthly'|'target_number_quarterly'|'target_number_yearly'} attr */
function numFieldForTargetLevel(kr, roleId, titleId, attr) {
  const t = (kr.targets || []).find(
    (x) =>
      String(targetRowJobRoleId(x) ?? '') === String(roleId) &&
      String(targetRowTitleId(x) ?? '') === String(titleId)
  );
  if (!t) return null;
  const v = t[attr];
  if (v == null || v === '') return null;
  const n = Math.round(parseFloat(v));
  return Number.isFinite(n) ? n : null;
}

const CATEGORY_TOTAL_EPS = 0.02;

/** Catalog KRs in this category that have at least one target row for the job role (tab). */
function relevantKrsForCategoryRole(keyResults, categoryId, roleId) {
  if (!categoryId || !roleId) return [];
  return keyResults.filter((kr) => {
    const cid = kr.category_id || kr.category?.id;
    if (String(cid) !== String(categoryId)) return false;
    return (kr.targets || []).some((t) => String(targetRowJobRoleId(t) ?? '') === String(roleId));
  });
}

function sumCategoryLevel(keyResults, categoryId, roleId, levelId) {
  let s = 0;
  for (const kr of relevantKrsForCategoryRole(keyResults, categoryId, roleId)) {
    s += num(pctForTargetLevel(kr, roleId, levelId)) || 0;
  }
  return s;
}

/** @param {string} orderBy kr_code | name | category | level:<uuid> (level sort needs activeRoleId) */
function compareCatalogRows(a, b, orderBy, order, sortContext = {}) {
  let cmp = 0;
  if (orderBy === 'kr_code') {
    cmp = String(a.kr_code || '').localeCompare(String(b.kr_code || ''), undefined, { numeric: true, sensitivity: 'base' });
  } else if (orderBy === 'name') {
    cmp = String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
  } else if (orderBy === 'category') {
    cmp = categoryNameOf(a).localeCompare(categoryNameOf(b), undefined, { sensitivity: 'base' });
  } else if (orderBy.startsWith('level:')) {
    const lid = orderBy.slice(6);
    const rid = sortContext.activeRoleId;
    const pa = rid ? pctForTargetLevel(a, rid, lid) : null;
    const pb = rid ? pctForTargetLevel(b, rid, lid) : null;
    const na = pa != null && pa > 0 ? pa : -1;
    const nb = pb != null && pb > 0 ? pb : -1;
    cmp = na - nb;
    if (cmp === 0) {
      cmp = String(a.kr_code || '').localeCompare(String(b.kr_code || ''), undefined, { numeric: true });
    }
  }
  if (cmp !== 0) return order === 'desc' ? -cmp : cmp;
  return 0;
}

/** Column widths (px); min widths — table scrolls horizontally as one unit (no sticky columns). */
const COL = { sn: 56, id: 220, name: 280, notes: 260, cat: 200, role: 200, add: 56, actions: 160 };

const sortLabelLeftSx = {
  width: '100%',
  maxWidth: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 1.2,
  margin: 0,
  padding: 0,
  '&.MuiTableSortLabel-root': {
    width: '100%',
    justifyContent: 'flex-start',
    gap: 0.25
  },
  '& .MuiTableSortLabel-icon': { marginLeft: 0.25, flexShrink: 0 }
};

const sortLabelCenterSx = {
  ...sortLabelLeftSx,
  justifyContent: 'center',
  textAlign: 'center',
  mx: 'auto'
};

const cellNoWrap = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

/** Matrix-style editors: one row per employee level (create dialog + right panel). */
function TargetsEntryTable({
  levels,
  disabled,
  weightByLevel,
  onWeightChange,
  numberByLevel,
  onNumberChange,
  periodByLevel,
  onPeriodChange,
  frequency = DEFAULT_KEY_RESULT_FREQ,
  onFrequencyChange = null,
  readOnlyFrequency = false,
  onApplyFromDaily = null
}) {
  if (!levels.length) return null;
  const f = normalizeKeyResultFreq(frequency);
  const tfCell = { width: 1, minWidth: 90, '& .MuiInputBase-input': { py: 0.75, px: 0.75, fontSize: '0.8125rem' } };
  const allFive =
    f.daily && f.weekly && f.monthly && f.quarterly && f.yearly;
  return (
    <Box sx={{ mt: 1 }}>
      {onFrequencyChange && (
        <Box sx={{ mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 700, display: 'block', mb: 0.5 }}>
            Key result frequency
          </FormLabel>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1.45 }}>
            Turn off periods you do not use for this Key Result. Only selected columns stay editable. Use{' '}
            <strong>All</strong> (all five) to set every period; then you can <strong>fill from daily</strong> (weekly = daily×5,
            monthly ≈ daily×22, etc.).
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {(['daily', 'weekly', 'monthly', 'quarterly', 'yearly']).map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(f[key])}
                    onChange={(e) => {
                      const next = { ...f, [key]: e.target.checked };
                      onFrequencyChange(normalizeKeyResultFreq(next));
                    }}
                    disabled={disabled || readOnlyFrequency}
                  />
                }
                label={key === 'daily' ? 'Daily' : key === 'weekly' ? 'Weekly' : key === 'monthly' ? 'Monthly' : key === 'quarterly' ? 'Quarterly' : 'Yearly'}
              />
            ))}
          </Box>
          {onApplyFromDaily && allFive && !disabled && !readOnlyFrequency && (
            <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={onApplyFromDaily} type="button">
              Fill W/M/Q/Y from daily (W=daily×{FROM_DAILY_MULT.weekly}, M≈×{FROM_DAILY_MULT.monthly}, …)
            </Button>
          )}
        </Box>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1000, tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Employee level</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 100 }}>
                Cat in %
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 96 }}>
                Daily
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 96 }}>
                Weekly
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 96 }}>
                Monthly
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 100 }}>
                Quarterly
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 96 }}>
                Yearly
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels.map((lvl) => {
              const pd = periodByLevel[lvl.id] || emptyPeriodDraft();
              return (
                <TableRow key={lvl.id} hover>
                  <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{lvl.name}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, verticalAlign: 'middle' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={weightByLevel[lvl.id] ?? ''}
                      onChange={(e) => onWeightChange(lvl.id, e.target.value)}
                      disabled={disabled}
                      inputProps={{ min: 0, max: 100, step: 1 }}
                      sx={tfCell}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, verticalAlign: 'middle' }}>
                    <TextField
                      size="small"
                      type="number"
                      value={numberByLevel[lvl.id] ?? ''}
                      onChange={(e) => onNumberChange(lvl.id, e.target.value)}
                      disabled={disabled || !f.daily}
                      inputProps={{ min: 0, step: 1 }}
                      sx={tfCell}
                    />
                  </TableCell>
                  {(['weekly', 'monthly', 'quarterly', 'yearly']).map((pk) => (
                    <TableCell key={pk} align="right" sx={{ py: 0.5, verticalAlign: 'middle' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={pd[pk] ?? ''}
                        onChange={(e) => onPeriodChange(lvl.id, pk, e.target.value)}
                        disabled={disabled || !f[pk]}
                        inputProps={{ min: 0, step: 1 }}
                        sx={tfCell}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Right-edge drag handle to resize the column (header only). */
function ColumnResizeHandle({ onResizeStart }) {
  return (
    <Box
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onResizeStart(e);
      }}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      sx={{
        position: 'absolute',
        right: 0,
        top: 0,
        width: 8,
        mr: -1,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 25,
        touchAction: 'none',
        '&:hover': { bgcolor: 'primary.main', opacity: 0.25 }
      }}
    />
  );
}

/** Excel-style grid: borders, min widths, sticky header row; horizontal scroll (no pinned edge columns). */
function CatalogMatrixTable({
  sortedKeyResults,
  /** Columns: one per Employee Level for the selected job role tab */
  levelColumns,
  activeRoleId,
  hasAnyRows,
  orderBy,
  order,
  onRequestSort,
  showRowActions,
  canView,
  canModify,
  canDelete,
  onView,
  onEdit,
  onRemove,
  columnWidths,
  setColumnWidths,
  stickyHeader = true,
  /** Outer scroll frame height so overflow + scrollbars reliably appear */
  scrollFrameSx = {},
  containerSx = {},
  /** Click row to open target panel (main page only) */
  onRowClick,
  selectedKrId,
  /** True when catalog has KRs but none have targets for the active tab */
  noRowsInTabHint = false,
  showAddColumn = false,
  onAddRow,
  canAdd = false,
  visibleCols = {}
}) {
  const theme = useTheme();
  const w = columnWidths;
  const addW = w.add ?? COL.add;
  const roleW = useCallback((colId) => w.roles[colId] ?? COL.role, [w.roles]);
  const showKrId = visibleCols.krId !== false;
  const showKrName = visibleCols.krName !== false;
  const showKrNotes = visibleCols.notes !== false;
  const showCategory = visibleCols.category !== false;
  const staticLeftColCount =
    1 + (showKrId ? 1 : 0) + (showKrName ? 1 : 0) + (showKrNotes ? 1 : 0) + (showCategory ? 1 : 0);

  const startResizeFixed = useCallback(
    (key, minW, maxW) => (e) => {
      const startX = e.clientX;
      const startWidth = w[key] ?? COL[key] ?? 72;
      const onMove = (ev) => {
        const next = clamp(Math.round(startWidth + (ev.clientX - startX)), minW, maxW);
        setColumnWidths((prev) => ({ ...prev, [key]: next }));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [setColumnWidths, w]
  );

  const startResizeRole = useCallback(
    (colId) => (e) => {
      const startX = e.clientX;
      const startWidth = w.roles[colId] ?? COL.role;
      const onMove = (ev) => {
        const next = clamp(Math.round(startWidth + (ev.clientX - startX)), 56, 520);
        setColumnWidths((prev) => ({
          ...prev,
          roles: { ...prev.roles, [colId]: next }
        }));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [setColumnWidths, w.roles]
  );
  const pctForCell = (kr, levelId) => {
    if (!activeRoleId || !levelId) return null;
    return pctForTargetLevel(kr, activeRoleId, levelId);
  };
  const numForCell = (kr, levelId) => {
    if (!activeRoleId || !levelId) return null;
    return numForTargetLevel(kr, activeRoleId, levelId);
  };
  const categoryOf = (kr) => kr.category || {};

  const categoryGroups = useMemo(() => {
    const order = [];
    const seen = new Set();
    for (const kr of sortedKeyResults) {
      const cid = kr.category_id || kr.category?.id || '__none__';
      if (!seen.has(cid)) {
        seen.add(cid);
        order.push(cid);
      }
    }
    return order.map((cid) => ({
      categoryId: cid,
      rows: sortedKeyResults.filter(
        (kr) => String(kr.category_id || kr.category?.id || '__none__') === String(cid)
      )
    }));
  }, [sortedKeyResults]);

  const headerBg = theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900];
  const minTableWidth =
    w.sn +
    (showKrId ? w.id : 0) +
    (showKrName ? w.name : 0) +
    (showKrNotes ? (w.notes ?? COL.notes) : 0) +
    (showCategory ? w.cat : 0) +
    (levelColumns || []).reduce((sum, lvl) => sum + roleW(lvl.id), 0) +
    (showAddColumn && canAdd ? addW : 0) +
    (showRowActions ? w.actions : 0);

  const headCellWrap = { position: 'relative' };

  const borderGrid = (bg) => ({
    border: `1px solid ${theme.palette.divider}`,
    boxSizing: 'border-box',
    bgcolor: bg
  });

  /** Sticky top is handled by MUI Table `stickyHeader`; do not add per-cell horizontal sticky (breaks column alignment). */
  const headerBase = {
    fontWeight: 700,
    verticalAlign: 'middle',
    py: 1,
    pl: 0.75,
    pr: '10px',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    ...borderGrid(headerBg)
  };

  const scrollThumb = theme.palette.mode === 'light' ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)';
  const scrollTrack = theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[900];

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 220,
        height: 'calc(100dvh - 300px)',
        maxHeight: 'min(78dvh, calc(100dvh - 220px))',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        boxSizing: 'border-box',
        ...scrollFrameSx
      }}
    >
      <TableContainer
        component="div"
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'scroll',
          WebkitOverflowScrolling: 'touch',
          scrollbarGutter: 'stable',
          scrollbarWidth: 'auto',
          '&::-webkit-scrollbar': { width: 14, height: 14 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: scrollThumb,
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'padding-box'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: scrollTrack,
            borderRadius: 1
          },
          '&::-webkit-scrollbar-corner': {
            background: scrollTrack
          },
          ...containerSx
        }}
      >
      <Table
        size="small"
        stickyHeader={stickyHeader}
        sx={{
          borderCollapse: 'collapse',
          width: 'max-content',
          minWidth: minTableWidth,
          tableLayout: 'auto'
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              sx={{
                ...headerBase,
                ...headCellWrap,
                minWidth: w.sn,
                width: w.sn,
                textAlign: 'center',
                overflow: 'hidden',
                bgcolor: headerBg
              }}
            >
              S.No
              <ColumnResizeHandle onResizeStart={startResizeFixed('sn', 40, 120)} />
            </TableCell>
            {showKrId && (
              <TableCell
                align="left"
                sortDirection={orderBy === 'kr_code' ? order : false}
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  minWidth: w.id,
                  width: w.id,
                  textAlign: 'left',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  bgcolor: headerBg,
                  ...cellNoWrap,
                  verticalAlign: 'middle'
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => onRequestSort('kr_code')}
                  sx={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    p: 0,
                    m: 0,
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 0.5,
                    pr: 1.5,
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                    boxSizing: 'border-box',
                    overflow: 'hidden'
                  }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    KR ID
                  </Typography>
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      flexShrink: 0,
                      opacity: orderBy === 'kr_code' ? 0.9 : 0.35,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      lineHeight: 1
                    }}
                  >
                    {orderBy === 'kr_code' ? (order === 'asc' ? '↑' : '↓') : '⇅'}
                  </Box>
                </Box>
                <ColumnResizeHandle onResizeStart={startResizeFixed('id', 120, 480)} />
              </TableCell>
            )}
            {showKrName && (
              <TableCell
                sortDirection={orderBy === 'name' ? order : false}
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  minWidth: w.name,
                  width: w.name,
                  overflow: 'hidden',
                  bgcolor: headerBg
                }}
              >
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => onRequestSort('name')}
                  sx={{
                    ...sortLabelLeftSx,
                    maxWidth: '100%',
                    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
                  }}
                >
                  Key Result name
                </TableSortLabel>
                <ColumnResizeHandle onResizeStart={startResizeFixed('name', 100, 520)} />
              </TableCell>
            )}
            {showKrNotes && (
              <TableCell
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  minWidth: w.notes ?? COL.notes,
                  width: w.notes ?? COL.notes,
                  overflow: 'hidden',
                  bgcolor: headerBg
                }}
              >
                Notes
                <ColumnResizeHandle onResizeStart={startResizeFixed('notes', 120, 680)} />
              </TableCell>
            )}
            {showCategory && (
              <TableCell
                sortDirection={orderBy === 'category' ? order : false}
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  minWidth: w.cat,
                  width: w.cat,
                  overflow: 'hidden',
                  bgcolor: headerBg
                }}
              >
                <TableSortLabel
                  active={orderBy === 'category'}
                  direction={orderBy === 'category' ? order : 'asc'}
                  onClick={() => onRequestSort('category')}
                  sx={{ ...sortLabelLeftSx, maxWidth: '100%' }}
                >
                  Key Result Category
                </TableSortLabel>
                <ColumnResizeHandle onResizeStart={startResizeFixed('cat', 80, 400)} />
              </TableCell>
            )}
            {(levelColumns || []).map((lvl) => (
              <TableCell
                key={lvl.id}
                align="center"
                sortDirection={orderBy === `level:${lvl.id}` ? order : false}
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  textAlign: 'center',
                  minWidth: roleW(lvl.id),
                  width: roleW(lvl.id),
                  overflow: 'visible',
                  whiteSpace: 'normal',
                  bgcolor: headerBg
                }}
              >
                <TableSortLabel
                  active={orderBy === `level:${lvl.id}`}
                  direction={orderBy === `level:${lvl.id}` ? order : 'asc'}
                  onClick={() => onRequestSort(`level:${lvl.id}`)}
                  sx={{
                    ...sortLabelCenterSx,
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'clip',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    '& .MuiTableSortLabel-icon': { flexShrink: 0 }
                  }}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    title={lvl.name}
                    sx={{
                      fontWeight: 700,
                      display: 'block',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      maxWidth: '100%',
                      lineHeight: 1.25
                    }}
                  >
                    {lvl.name}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      fontWeight: 600,
                      mt: 0.25,
                      lineHeight: 1.25,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      maxWidth: '100%'
                    }}
                  >
                    Cat % / Daily, Weekly, Monthly, Quarterly, Yearly
                  </Typography>
                </TableSortLabel>
                <ColumnResizeHandle onResizeStart={startResizeRole(lvl.id)} />
              </TableCell>
            ))}
            {showAddColumn && canAdd && (
              <TableCell
                align="center"
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  textAlign: 'center',
                  minWidth: addW,
                  width: addW,
                  overflow: 'hidden',
                  bgcolor: headerBg
                }}
              >
                <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                  +Add
                </Typography>
                <ColumnResizeHandle onResizeStart={startResizeFixed('add', 44, 140)} />
              </TableCell>
            )}
            {showRowActions && (
              <TableCell
                align="center"
                sx={{
                  ...headerBase,
                  ...headCellWrap,
                  whiteSpace: 'nowrap',
                  minWidth: w.actions,
                  width: w.actions,
                  overflow: 'hidden',
                  bgcolor: headerBg
                }}
              >
                Actions
                <ColumnResizeHandle onResizeStart={startResizeFixed('actions', 72, 260)} />
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!hasAnyRows ? (
            <TableRow>
              <TableCell
                colSpan={
                  staticLeftColCount +
                  (levelColumns || []).length +
                  (showAddColumn && canAdd ? 1 : 0) +
                  (showRowActions ? 1 : 0)
                }
                sx={borderGrid('transparent')}
              >
                <Box>
                  {noRowsInTabHint ? (
                    <>
                      <Typography color="text.secondary" sx={{ mb: 1 }}>
                        No Key Results include targets for <strong>this job role tab</strong> yet. Other tabs may still
                        have data for the same catalog lines. Use <strong>+Add</strong> to add a line for this role, or
                        open another tab to see KRs configured there.
                      </Typography>
                      {canAdd && onAddRow && levelColumns?.length > 0 && (
                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => onAddRow()}>
                          Create Key Result (this job role tab)
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography color="text.secondary" sx={{ mb: 1 }}>
                        No Key Results yet for this view{levelColumns?.length ? '.' : ' (add Employee Levels for this job role first).'}
                      </Typography>
                      {canAdd && onAddRow && levelColumns?.length > 0 && (
                        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => onAddRow()}>
                          Create Key Result (this job role tab)
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            (() => {
              let rowNum = 0;
              return categoryGroups.map((group) => {
                const firstKr = group.rows[0];
                const cat = firstKr ? categoryOf(firstKr) : {};
                const catHex = cat.theme_color || '#6366f1';
                const catTint = categoryRowBg(catHex, false);
                const catId = firstKr?.category_id || firstKr?.category?.id || '__none__';
                const catName = catId === '__none__' ? 'Uncategorized' : cat.name || '—';
                const portW = num(cat.weight_percent);
                const catHeaderBase = {
                  ...borderGrid(catTint),
                  verticalAlign: 'middle',
                  py: 0.75,
                  pl: 0.75,
                  pr: '10px',
                  borderLeft: `4px solid ${catHex}`
                };
                return (
                  <React.Fragment key={`grp-${group.categoryId}`}>
                    <TableRow hover={false}>
                      <TableCell colSpan={staticLeftColCount} sx={catHeaderBase}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            flexWrap: 'wrap',
                            rowGap: 0.75
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: catHex }}>
                              {catName}
                              {portW > 0 ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{ ml: 1, fontWeight: 600, opacity: 0.9 }}
                                >
                                  (portfolio {Number.isInteger(portW) ? portW.toFixed(0) : portW.toFixed(1)}%)
                                </Typography>
                              ) : null}
                            </Typography>
                            {cat.description ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.5, lineHeight: 1.4, fontWeight: 500 }}
                              >
                                {cat.description}
                              </Typography>
                            ) : null}
                          </Box>
                          {canAdd && showAddColumn && onAddRow && catId !== '__none__' && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddRow(String(catId));
                              }}
                              type="button"
                            >
                              Create Key Result
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                      {(levelColumns || []).map((lvl) => {
                        const sum = sumCategoryLevel(sortedKeyResults, catId, activeRoleId, lvl.id);
                        const ok = Math.abs(sum - 100) <= CATEGORY_TOTAL_EPS;
                        return (
                          <TableCell key={lvl.id} align="center" sx={{ ...catHeaderBase, textAlign: 'center' }}>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                fontVariantNumeric: 'tabular-nums',
                                fontWeight: 700,
                                color: ok ? 'success.main' : 'warning.main'
                              }}
                            >
                              ({Math.round(sum)}%)
                            </Typography>
                          </TableCell>
                        );
                      })}
                      {showAddColumn && canAdd && (
                        <TableCell
                          align="center"
                          sx={{
                            ...catHeaderBase,
                            minWidth: addW,
                            width: addW
                          }}
                        />
                      )}
                      {showRowActions && (
                        <TableCell
                          align="center"
                          sx={{
                            ...catHeaderBase,
                            minWidth: w.actions,
                            width: w.actions
                          }}
                        />
                      )}
                    </TableRow>
                    {group.rows.map((kr) => {
              rowNum += 1;
              const rowIndex = rowNum - 1;
              const catRow = categoryOf(kr);
              const catHexRow = catRow.theme_color || '#6366f1';
              const rowBg = categoryRowBg(catHexRow, false);
              const rowBgHover = categoryRowBg(catHexRow, true);
              const cellBg = (extra = {}) => ({
                ...borderGrid(rowBg),
                ...extra,
                verticalAlign: 'middle',
                py: 0.75,
                pl: 0.75,
                pr: '10px'
              });
              const rowSelected = selectedKrId && String(selectedKrId) === String(kr.id);
              return (
                <TableRow
                  key={kr.id}
                  onClick={onRowClick ? () => onRowClick(kr) : undefined}
                  selected={Boolean(rowSelected)}
                  hover
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    ...(rowSelected
                      ? {
                          '& td': {
                            boxShadow: (th) => `inset 0 0 0 2px ${th.palette.primary.main}`
                          }
                        }
                      : {}),
                    '&:hover td': {
                      bgcolor: `${rowBgHover} !important`
                    }
                  }}
                >
                  <TableCell
                    align="center"
                    sx={{
                      ...cellBg({
                        minWidth: w.sn,
                        width: w.sn,
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 600
                      })
                    }}
                  >
                    {rowIndex + 1}
                  </TableCell>
                  {showKrId && (
                    <TableCell
                      align="left"
                      sx={{
                        ...cellBg({
                          minWidth: w.id,
                          width: w.id,
                          textAlign: 'left',
                          pl: 1.5,
                          boxShadow: `inset 4px 0 0 ${catHexRow}`,
                          boxSizing: 'border-box',
                          overflow: 'hidden'
                        })
                      }}
                    >
                      <Typography
                        variant="caption"
                        component="div"
                        title={kr.kr_code || ''}
                        sx={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontWeight: 600,
                          width: '100%',
                          maxWidth: '100%',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {kr.kr_code}
                      </Typography>
                    </TableCell>
                  )}
                  {showKrName && (
                    <TableCell
                      sx={{
                        ...cellBg({
                          minWidth: w.name,
                          width: w.name,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere'
                        })
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                        {kr.name}
                      </Typography>
                    </TableCell>
                  )}
                  {showKrNotes && (
                    <TableCell
                      sx={{
                        ...cellBg({
                          minWidth: w.notes ?? COL.notes,
                          width: w.notes ?? COL.notes,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere'
                        })
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                        {kr.description || '—'}
                      </Typography>
                    </TableCell>
                  )}
                  {showCategory && (
                    <TableCell
                      sx={{
                        ...cellBg({ minWidth: w.cat, width: w.cat, ...cellNoWrap })
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        title={
                          catRow.category_code
                            ? `${catRow.name || '—'} · code ${catRow.category_code}`
                            : catRow.name || '—'
                        }
                        sx={{ fontWeight: 600, color: catHexRow, fontSize: '0.8125rem' }}
                      >
                        {catRow.name || '—'}
                      </Typography>
                    </TableCell>
                  )}
                  {(levelColumns || []).map((lvl) => {
                    const p = pctForCell(kr, lvl.id);
                    const nv = numForCell(kr, lvl.id);
                    const pw = activeRoleId
                      ? numFieldForTargetLevel(kr, activeRoleId, lvl.id, 'target_number_weekly')
                      : null;
                    const pm = activeRoleId
                      ? numFieldForTargetLevel(kr, activeRoleId, lvl.id, 'target_number_monthly')
                      : null;
                    const pq = activeRoleId
                      ? numFieldForTargetLevel(kr, activeRoleId, lvl.id, 'target_number_quarterly')
                      : null;
                    const py = activeRoleId
                      ? numFieldForTargetLevel(kr, activeRoleId, lvl.id, 'target_number_yearly')
                      : null;
                    const fmtP = (x) => (x != null ? x : '—');
                    return (
                      <TableCell
                        key={lvl.id}
                        align="center"
                        sx={{
                          ...cellBg({
                            textAlign: 'center',
                            minWidth: roleW(lvl.id),
                            width: roleW(lvl.id),
                            verticalAlign: 'middle',
                            whiteSpace: 'normal'
                          })
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.2,
                            py: 0.25,
                            px: 0.25
                          }}
                        >
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              fontWeight: p != null && p > 0 ? 700 : 500,
                              fontSize: '0.8125rem',
                              lineHeight: 1.2
                            }}
                          >
                            {p != null && p > 0 ? `${p}%` : '—'}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              lineHeight: 1.2
                            }}
                            title="Daily target #"
                          >
                            D: {nv != null ? nv : '—'}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              fontVariantNumeric: 'tabular-nums',
                              color: 'text.disabled',
                              fontSize: '0.62rem',
                              lineHeight: 1.15,
                              textAlign: 'center',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word'
                            }}
                            title="Weekly, Monthly, Quarterly, Yearly target #"
                          >
                            W {fmtP(pw)} · M {fmtP(pm)} · Q {fmtP(pq)} · Y {fmtP(py)}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  })}
                  {showAddColumn && canAdd && (
                    <TableCell
                      align="center"
                      sx={{
                        ...cellBg({
                          textAlign: 'center',
                          minWidth: addW,
                          width: addW,
                          verticalAlign: 'middle',
                          py: 0.25
                        })
                      }}
                    >
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddRow?.();
                        }}
                        aria-label="Add Key Result for this job role"
                        type="button"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                  {showRowActions && (
                    <TableCell
                      align="center"
                      sx={{
                        ...cellBg({
                          minWidth: w.actions,
                          width: w.actions,
                          whiteSpace: 'nowrap',
                          verticalAlign: 'middle',
                          py: 0.5,
                          px: 0.5
                        })
                      }}
                    >
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, flexWrap: 'nowrap' }}>
                      {canView && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onView(kr);
                          }}
                          aria-label="View Key Result"
                          type="button"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canModify && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(kr);
                          }}
                          aria-label="Edit Key Result (all roles)"
                          type="button"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {canDelete && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(kr);
                          }}
                          aria-label="Remove this job role from Key Result"
                          type="button"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
                    })}
                  </React.Fragment>
                );
              });
            })()
          )}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  );
}

export default function KeyResultsMatrix() {
  const { hasPermission } = useAuth();
  const canAdd = hasPermission('okr_add');
  const canView = hasPermission('okr_view');
  const canModify = hasPermission('okr_modify');
  const canDelete = hasPermission('okr_delete');
  /** Show row actions if the user can at least view details, or change/delete catalog rows */
  const showRowActions = canView || canModify || canDelete;

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const keyResultsRef = useRef([]);
  useEffect(() => {
    keyResultsRef.current = keyResults;
  }, [keyResults]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api
      .get('/okr/key-result-name-templates')
      .then((r) => setOkrTemplateLibrary(r.data?.data?.templates || []))
      .catch(() => {});
  }, []);

  const [filterCategoryId, setFilterCategoryId] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  /** When true, dialog is scoped to the active job-role tab (targets for other roles preserved on save). */
  const [dialogTabScoped, setDialogTabScoped] = useState(false);
  const [editingKr, setEditingKr] = useState(null);
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  /** roleId -> { [employeeTitleId]: string percent } */
  const [targetByRoleLevel, setTargetByRoleLevel] = useState({});
  /** roleId -> { [employeeTitleId]: string target number } */
  const [targetNumberByRoleLevel, setTargetNumberByRoleLevel] = useState({});
  /** roleId -> titleId -> { weekly, monthly, quarterly, yearly } draft strings */
  const [targetPeriodByRoleLevel, setTargetPeriodByRoleLevel] = useState({});
  const [saving, setSaving] = useState(false);
  /** create | edit | view */
  const [dialogMode, setDialogMode] = useState('create');
  /** Default: KR Id ascending */
  const [orderBy, setOrderBy] = useState('kr_code');
  const [order, setOrder] = useState('asc');
  const [matrixDialogOpen, setMatrixDialogOpen] = useState(false);
  /** Horizontal tabs: one per employee job role; columns = Employee Levels for that role */
  const [roleTabIndex, setRoleTabIndex] = useState(0);
  const [levelsByRoleId, setLevelsByRoleId] = useState({});
  /** Right panel: selected catalog KR for the active tab */
  const [selectedKrId, setSelectedKrId] = useState(null);
  /** levelId -> category weight % string for the target panel */
  const [panelDraft, setPanelDraft] = useState({});
  /** levelId -> target number string for the target panel */
  const [panelNumberDraft, setPanelNumberDraft] = useState({});
  /** levelId -> { weekly, monthly, quarterly, yearly } for the target panel */
  const [panelPeriodDraft, setPanelPeriodDraft] = useState({});
  const [panelSaving, setPanelSaving] = useState(false);
  /** Sticky row: which of Daily…Yearly are used for this catalog key result */
  const [panelFrequency, setPanelFrequency] = useState({ ...DEFAULT_KEY_RESULT_FREQ });
  const [formKeyResultFrequency, setFormKeyResultFrequency] = useState({ ...DEFAULT_KEY_RESULT_FREQ });
  /** Row click: (a) modal dialog (wide) or (b) full-height side sheet (matrix hidden until close) */
  const [keyResultRowMode, setKeyResultRowMode] = useState(() => {
    try {
      return localStorage.getItem(LS_KR_ROW_MODE) === 'popup' ? 'popup' : 'side';
    } catch {
      return 'side';
    }
  });
  const [keyResultTargetDialogOpen, setKeyResultTargetDialogOpen] = useState(false);
  /** Server rows: published catalog weights per job role */
  const [catalogPublishByRole, setCatalogPublishByRole] = useState([]);
  const [publishBusy, setPublishBusy] = useState(false);
  /** 0 = pick templates / working set; 1 = matrix / cat % (per job role). */
  const [definerSubTab, setDefinerSubTab] = useState(0);
  const [templateBusyId, setTemplateBusyId] = useState(null);
  /** Template ids (library) marked inactive — cannot add to catalog until reactivated */
  const [inactiveTemplateIds, setInactiveTemplateIds] = useState([]);
  /** Shared between main grid and matrix popup; drag column edges in the header to resize. */
  const [columnWidths, setColumnWidths] = useState({
    sn: COL.sn,
    id: COL.id,
    name: COL.name,
    notes: COL.notes,
    cat: COL.cat,
    add: COL.add,
    actions: COL.actions,
    roles: {}
  });

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get('/okr/categories');
      setCategoryOptions(r.data?.data || []);
    } catch {
      setCategoryOptions([]);
    }
  }, []);

  const categories = useMemo(
    () => categoryOptions.filter((c) => c.is_active !== false),
    [categoryOptions]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogSettled, contactsSettled] = await Promise.allSettled([
        /* Always load full catalog: template picker and “added” need cross-category; filter is client-side */
        api.get('/okr/catalog-key-results'),
        api.get('/contacts/employee-job-roles')
      ]);
      const catalogData =
        catalogSettled.status === 'fulfilled' ? catalogSettled.value.data?.data : null;
      const rolesFromContacts =
        contactsSettled.status === 'fulfilled' ? contactsSettled.value.data?.data || [] : [];

      if (catalogSettled.status === 'rejected') {
        toast.error(
          catalogSettled.reason?.response?.data?.message || 'Failed to load Key Results'
        );
        setKeyResults([]);
        keyResultsRef.current = [];
        setCatalogPublishByRole([]);
      } else {
        const krs = catalogData?.key_results || [];
        setKeyResults(krs);
        keyResultsRef.current = krs;
        setCatalogPublishByRole(catalogData?.catalog_publish_by_role || []);
        if (catalogData?.catalog_unavailable) {
          toast.warn(
            'Key Results catalog is not available. Apply SQL migrations through rebuild-database (includes 040+041 for catalog DDL) or check the backend log for the exact DB error.'
          );
        }
      }

      setRoles(
        rolesFromContacts.length
          ? rolesFromContacts
          : catalogData?.employee_job_roles || []
      );

      try {
        const tpl = await api.get('/okr/key-result-template-library-state', { skipErrorToast: true });
        setInactiveTemplateIds(tpl.data?.data?.inactive_template_ids || []);
      } catch {
        setInactiveTemplateIds([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Key Results');
      setKeyResults([]);
      keyResultsRef.current = [];
      setCatalogPublishByRole([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!roles.length) {
      setLevelsByRoleId({});
      return;
    }
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        roles.map((r) =>
          api
            .get('/contacts/employee-titles', { params: { employee_job_role_id: r.id } })
            .then((res) => ({ roleId: r.id, levels: res.data?.data || [] }))
            .catch(() => ({ roleId: r.id, levels: [] }))
        )
      );
      if (cancelled) return;
      const m = {};
      pairs.forEach((p) => {
        m[p.roleId] = p.levels;
      });
      setLevelsByRoleId(m);
    })();
    return () => {
      cancelled = true;
    };
  }, [roles]);

  const sortedRoles = useMemo(
    () =>
      [...roles].sort(
        (a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)
      ),
    [roles]
  );

  const activeRole = sortedRoles[roleTabIndex] || null;

  const levelColumnsForTab = useMemo(() => {
    if (!activeRole) return [];
    const raw = levelsByRoleId[activeRole.id] || [];
    return [...raw].sort(
      (a, b) =>
        Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
        String(a.name || '').localeCompare(String(b.name || ''))
    );
  }, [activeRole, levelsByRoleId]);

  const publishRecordForActiveRole = useMemo(() => {
    if (!activeRole?.id) return null;
    return (
      catalogPublishByRole.find((p) => String(p.employee_job_role_id) === String(activeRole.id)) || null
    );
  }, [catalogPublishByRole, activeRole?.id]);

  useEffect(() => {
    setDefinerSubTab(0);
  }, [activeRole?.id]);

  const isTemplateRowAdded = useCallback(
    (t) => {
      if (!activeRole?.id) return false;
      const ex = findCatalogKeyResultForTemplateInLibraryFamily(t, keyResults, categoryOptions, {
        includeNameMatchCUST: false
      });
      if (!ex) return false;
      return catalogRowHasTargetForJobRole(ex, activeRole.id);
    },
    [keyResults, categoryOptions, activeRole?.id]
  );

  const getCatalogRowForTemplate = useCallback(
    (t) =>
      findCatalogKeyResultForTemplateInLibraryFamily(t, keyResults, categoryOptions, {
        includeNameMatchCUST: true
      }),
    [keyResults, categoryOptions]
  );

  const customCatalogRows = useMemo(
    () => keyResults.filter((kr) => isCustomCatalogLine(kr, categoryOptions)),
    [keyResults, categoryOptions]
  );

  const isCustomRowInWorkingSet = useCallback(
    (kr) => {
      if (!activeRole?.id || !kr) return false;
      return catalogRowHasTargetForJobRole(kr, activeRole.id);
    },
    [activeRole?.id]
  );

  const removeTemplateFromWorking = useCallback(
    async (t) => {
      if (!activeRole?.id || !canDelete) return;
      const kr = getCatalogRowForTemplate(t);
      if (!kr) return;
      if (!catalogRowHasTargetForJobRole(kr, activeRole.id)) return;
      setTemplateBusyId(t.id);
      try {
        await api.delete(`/okr/catalog-key-results/${kr.id}/employee-job-role/${activeRole.id}`);
        toast.success('Removed from this job role’s working set');
        if (selectedKrId === kr.id) {
          setSelectedKrId(null);
          setPanelDraft({});
          setPanelNumberDraft({});
          setPanelPeriodDraft({});
        }
        await loadData();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Remove failed');
      } finally {
        setTemplateBusyId(null);
      }
    },
    [activeRole?.id, canDelete, getCatalogRowForTemplate, loadData, selectedKrId]
  );

  const toggleTemplateLibraryActive = useCallback(
    async (templateId, nextActive) => {
      if (!canModify) return;
      try {
        await api.put(`/okr/key-result-template-library-state/${encodeURIComponent(templateId)}`, {
          is_active: nextActive
        });
        setInactiveTemplateIds((prev) => {
          const s = new Set(prev);
          if (nextActive) s.delete(templateId);
          else s.add(templateId);
          return [...s];
        });
        toast.success(nextActive ? 'Template activated in library.' : 'Template deactivated in library.');
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed to update template.');
      }
    },
    [canModify]
  );

  const addCustomCatalogToWorking = useCallback(
    async (kr) => {
      if (!canAdd || !activeRole?.id) {
        toast.error('You cannot add Key Results here, or no job role tab is selected.');
        return;
      }
      const levels = levelColumnsForTab;
      if (!levels.length) {
        toast.error('No employee levels for this job role. Configure them under Administration → Employee Settings.');
        return;
      }
      if (!kr?.is_active) {
        toast.warn('Activate this Key Result before adding it to a working set.');
        return;
      }
      if (isCustomRowInWorkingSet(kr)) {
        toast.info('This row is already in the working set for this job role.');
        return;
      }
      setTemplateBusyId(`custom-${kr.id}`);
      try {
        const existing = keyResultsRef.current.find((k) => String(k.id) === String(kr.id));
        if (!existing) return;
        const emptyW = {};
        const emptyN = {};
        const emptyP = {};
        levels.forEach((lvl) => {
          emptyW[lvl.id] = '';
          emptyN[lvl.id] = '';
          emptyP[lvl.id] = emptyPeriodDraft();
        });
        const role_targets = mergeRoleTargetsForTabSave(
          existing,
          activeRole.id,
          levels,
          emptyW,
          emptyN,
          emptyP
        );
        await api.put(`/okr/catalog-key-results/${existing.id}`, {
          category_id: existing.category_id,
          name: existing.name,
          description: existing.description ?? null,
          key_result_frequency: normalizeKeyResultFreq(existing.key_result_frequency),
          role_targets
        });
        toast.success('Added to this job role’s working set.');
        await loadData();
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || 'Add failed');
      } finally {
        setTemplateBusyId(null);
      }
    },
    [canAdd, activeRole?.id, activeRole, levelColumnsForTab, loadData, isCustomRowInWorkingSet]
  );

  const removeCustomCatalogFromWorking = useCallback(
    async (kr) => {
      if (!activeRole?.id || !canDelete) return;
      if (!isCustomRowInWorkingSet(kr)) return;
      setTemplateBusyId(`custom-${kr.id}`);
      try {
        await api.delete(`/okr/catalog-key-results/${kr.id}/employee-job-role/${activeRole.id}`);
        toast.success('Removed from this job role’s working set');
        if (selectedKrId === kr.id) {
          setSelectedKrId(null);
          setPanelDraft({});
          setPanelNumberDraft({});
          setPanelPeriodDraft({});
        }
        await loadData();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Remove failed');
      } finally {
        setTemplateBusyId(null);
      }
    },
    [activeRole?.id, canDelete, isCustomRowInWorkingSet, loadData, selectedKrId]
  );

  const toggleCustomCatalogActive = useCallback(
    async (kr, nextActive) => {
      if (!canModify) return;
      setTemplateBusyId(`active-${kr.id}`);
      try {
        await api.put(`/okr/catalog-key-results/${kr.id}`, { is_active: nextActive });
        await loadData();
        toast.success(nextActive ? 'Key Result activated.' : 'Key Result deactivated.');
      } catch (e) {
        toast.error(e.response?.data?.message || 'Update failed');
      } finally {
        setTemplateBusyId(null);
      }
    },
    [canModify, loadData]
  );

  const addKeyResultFromTemplate = useCallback(
    async (t, { batch = false, refreshKeyResults } = {}) => {
      const refresh = refreshKeyResults != null ? refreshKeyResults : loadData;
      if (!canAdd || !activeRole?.id) {
        if (!batch) toast.error('You cannot add Key Results here, or no job role tab is selected.');
        return;
      }
      if (inactiveTemplateIds.includes(t.id)) {
        if (!batch) toast.warn('This template is inactive in the library and cannot be added.');
        return;
      }
      const levels = levelColumnsForTab;
      if (!levels.length) {
        if (!batch) {
          toast.error('No employee levels for this job role. Configure them under Administration → Employee Settings.');
        }
        return;
      }
      const krs = keyResultsRef.current;
      const category =
        pickCategoryForNewTemplateCatalogRow(t, categories, krs) ||
        resolveOkrCategoryForTemplate(categories, t);
      if (!category) {
        if (!batch) toast.error(`Key Result category for code “${t.categoryCode}” was not found.`);
        return;
      }
      if (isTemplateRowAdded(t)) {
        if (!batch) toast.info('This template is already in the working set for this job role.');
        return;
      }
      if (!batch) setTemplateBusyId(t.id);
      try {
        const existing = findCatalogKeyResultForTemplateInLibraryFamily(t, krs, categoryOptions, {
          includeNameMatchCUST: true
        });
        const key_result_frequency = { ...DEFAULT_KEY_RESULT_FREQ };
        if (existing) {
          const emptyW = {};
          const emptyN = {};
          const emptyP = {};
          levels.forEach((lvl) => {
            emptyW[lvl.id] = '';
            emptyN[lvl.id] = '';
            emptyP[lvl.id] = emptyPeriodDraft();
          });
          const role_targets = mergeRoleTargetsForTabSave(
            existing,
            activeRole.id,
            levels,
            emptyW,
            emptyN,
            emptyP
          );
          await api.put(`/okr/catalog-key-results/${existing.id}`, {
            category_id: existing.category_id,
            name: existing.name,
            description: existing.description ?? null,
            key_result_frequency: normalizeKeyResultFreq(existing.key_result_frequency),
            source_template_id: existing.source_template_id || existing.sourceTemplateId || t.id,
            role_targets
          });
        } else {
          const role_targets = levels.map((lvl) => ({
            employee_job_role_id: activeRole.id,
            employee_title_id: lvl.id,
            target_percent: 0,
            target_number: 0,
            ...periodPayloadFromDraft(emptyPeriodDraft())
          }));
          await api.post('/okr/catalog-key-results', {
            category_id: category.id,
            name: t.name,
            description: t.description != null && String(t.description).trim() ? t.description : null,
            key_result_frequency,
            source_template_id: t.id,
            role_targets
          });
        }
        if (!batch) {
          toast.success('Added to this job role’s working set. Use step 2 to set Cat in % and targets.');
          await refresh();
        } else {
          await refresh();
        }
      } catch (e) {
        if (!batch) toast.error(e.response?.data?.message || e.message || 'Add failed');
        throw e;
      } finally {
        if (!batch) setTemplateBusyId(null);
      }
    },
    [canAdd, activeRole, levelColumnsForTab, categories, categoryOptions, isTemplateRowAdded, loadData, inactiveTemplateIds]
  );

  const handleAddAllTemplatesInCategory = useCallback(
    async (_categoryId, code) => {
      if (!canAdd || !activeRole?.id) return;
      const { filterTemplatesToExistingCategories } = await import('./okrKeyResultNameTemplates');
      const valid = filterTemplatesToExistingCategories(categoryOptions);
      const inCat = valid.filter((x) => x.categoryCode === code);
      const pending = inCat.filter((t) => !isTemplateRowAdded(t) && !inactiveTemplateIds.includes(t.id));
      if (pending.length === 0) {
        toast.info('All templates in this category are already added for this job role.');
        return;
      }
      setTemplateBusyId(`all-${code}`);
      try {
        for (const t of pending) {
          // eslint-disable-next-line no-await-in-loop
          await addKeyResultFromTemplate(t, { batch: true, refreshKeyResults: loadData });
        }
        toast.success(`Added ${pending.length} key result(s) to this job role’s working set.`);
      } catch {
        toast.error('Not all items could be added. Refresh and try the remaining ones.');
        await loadData();
      } finally {
        setTemplateBusyId(null);
      }
    },
    [addKeyResultFromTemplate, canAdd, loadData, isTemplateRowAdded, categoryOptions, inactiveTemplateIds, activeRole?.id]
  );

  const handlePublishCatalog = useCallback(async () => {
    if (!activeRole?.id || !canModify) return;
    setPublishBusy(true);
    try {
      await api.post('/okr/catalog-key-results/publish', { employee_job_role_id: activeRole.id });
      toast.success('Catalog published for this job role.');
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Publish failed');
    } finally {
      setPublishBusy(false);
    }
  }, [activeRole?.id, canModify, loadData]);

  const handleRevokePublish = useCallback(async () => {
    if (!activeRole?.id || !canModify || !publishRecordForActiveRole) return;
    setPublishBusy(true);
    try {
      await api.post('/okr/catalog-key-results/revoke-publish', { employee_job_role_id: activeRole.id });
      toast.success('Publish revoked.');
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Revoke failed');
    } finally {
      setPublishBusy(false);
    }
  }, [activeRole?.id, canModify, publishRecordForActiveRole, loadData]);

  useEffect(() => {
    if (sortedRoles.length > 0 && roleTabIndex >= sortedRoles.length) {
      setRoleTabIndex(0);
    }
  }, [sortedRoles, roleTabIndex]);

  useEffect(() => {
    setColumnWidths((prev) => {
      const nextRoles = { ...prev.roles };
      let changed = false;
      levelColumnsForTab.forEach((lvl) => {
        if (nextRoles[lvl.id] == null) {
          nextRoles[lvl.id] = COL.role;
          changed = true;
        }
      });
      return changed ? { ...prev, roles: nextRoles } : prev;
    });
  }, [levelColumnsForTab]);

  const openCreateForTab = (presetCategoryId) => {
    if (!activeRole) {
      toast.error('Select a job role tab first.');
      return;
    }
    const levels = levelsByRoleId[activeRole.id] || [];
    if (levels.length === 0) {
      toast.error(
        `Configure Employee Levels for “${activeRole.name}” under Administration → Employee Settings before adding a Key Result.`
      );
      return;
    }
    const preset =
      presetCategoryId != null && String(presetCategoryId).trim() !== ''
        ? String(presetCategoryId)
        : null;
    const resolvedCategoryId =
      preset && categories.some((c) => String(c.id) === preset) ? preset : categories[0]?.id || '';
    setDialogTabScoped(true);
    setDialogMode('create');
    setEditingKr(null);
    setFormCategoryId(resolvedCategoryId);
    setFormName('');
    setFormDescription('');
    setSelectedRoles([activeRole]);
    const m = { [activeRole.id]: {} };
    const mNum = { [activeRole.id]: {} };
    const mPer = { [activeRole.id]: {} };
    levels.forEach((lvl) => {
      m[activeRole.id][lvl.id] = '';
      mNum[activeRole.id][lvl.id] = '';
      mPer[activeRole.id][lvl.id] = emptyPeriodDraft();
    });
    setTargetByRoleLevel(m);
    setTargetNumberByRoleLevel(mNum);
    setTargetPeriodByRoleLevel(mPer);
    setFormKeyResultFrequency({ ...DEFAULT_KEY_RESULT_FREQ });
    setDialogOpen(true);
  };

  const hydrateTargetsFromKr = (kr) => {
    const t = kr.targets || [];
    const roleMap = new Map();
    t.forEach((x) => {
      const rid = x.employee_job_role_id;
      if (!rid) return;
      if (!roleMap.has(rid)) {
        roleMap.set(rid, x.employeeJobRole || { id: rid, name: '?' });
      }
    });
    setSelectedRoles(Array.from(roleMap.values()).filter((x) => x.id));
    const m = {};
    const mNum = {};
    const mPer = {};
    t.forEach((x) => {
      const rid = x.employee_job_role_id;
      const tid = x.employee_title_id;
      if (!rid || !tid) return;
      if (!m[rid]) m[rid] = {};
      if (!mNum[rid]) mNum[rid] = {};
      if (!mPer[rid]) mPer[rid] = {};
      m[rid][tid] =
        x.target_percent != null && x.target_percent !== ''
          ? String(intPercent0to100(x.target_percent))
          : '';
      mNum[rid][tid] =
        x.target_number != null && x.target_number !== '' ? String(intWhole(x.target_number)) : '';
      mPer[rid][tid] = periodDraftFromTargetRow(x);
    });
    setTargetByRoleLevel(m);
    setTargetNumberByRoleLevel(mNum);
    setTargetPeriodByRoleLevel(mPer);
    setFormKeyResultFrequency(normalizeKeyResultFreq(kr.key_result_frequency));
  };

  const openView = (kr) => {
    setDialogTabScoped(false);
    setDialogMode('view');
    setEditingKr(kr);
    setFormCategoryId(kr.category_id || kr.category?.id || '');
    setFormName(kr.name || '');
    setFormDescription(kr.description || '');
    hydrateTargetsFromKr(kr);
    setDialogOpen(true);
  };

  /** Full dialog: all job roles & levels (row Actions → Edit). */
  const openEditFull = (kr) => {
    setDialogTabScoped(false);
    setDialogMode('edit');
    setEditingKr(kr);
    setFormCategoryId(kr.category_id || kr.category?.id || '');
    setFormName(kr.name || '');
    setFormDescription(kr.description || '');
    hydrateTargetsFromKr(kr);
    setDialogOpen(true);
  };

  const onRolesChange = (_, newRoles) => {
    setSelectedRoles(newRoles);
    setTargetByRoleLevel((prev) => {
      const next = { ...prev };
      newRoles.forEach((r) => {
        if (!next[r.id]) next[r.id] = {};
        const levels = levelsByRoleId[r.id] || [];
        levels.forEach((lvl) => {
          if (next[r.id][lvl.id] === undefined) next[r.id][lvl.id] = '';
        });
      });
      Object.keys(next).forEach((id) => {
        if (!newRoles.find((r) => r.id === id)) delete next[id];
      });
      return next;
    });
    setTargetNumberByRoleLevel((prev) => {
      const next = { ...prev };
      newRoles.forEach((r) => {
        if (!next[r.id]) next[r.id] = {};
        const levels = levelsByRoleId[r.id] || [];
        levels.forEach((lvl) => {
          if (next[r.id][lvl.id] === undefined) next[r.id][lvl.id] = '';
        });
      });
      Object.keys(next).forEach((id) => {
        if (!newRoles.find((r) => r.id === id)) delete next[id];
      });
      return next;
    });
    setTargetPeriodByRoleLevel((prev) => {
      const next = { ...prev };
      newRoles.forEach((r) => {
        if (!next[r.id]) next[r.id] = {};
        const levels = levelsByRoleId[r.id] || [];
        levels.forEach((lvl) => {
          if (next[r.id][lvl.id] === undefined) next[r.id][lvl.id] = emptyPeriodDraft();
        });
      });
      Object.keys(next).forEach((id) => {
        if (!newRoles.find((r) => r.id === id)) delete next[id];
      });
      return next;
    });
  };

  const saveKr = async () => {
    if (dialogMode === 'view') return;
    if (!formCategoryId) {
      toast.error('Choose a Key Result Category.');
      return;
    }
    if (!String(formName).trim()) {
      toast.error('Enter a Key Result name.');
      return;
    }
    if (selectedRoles.length === 0) {
      toast.error('Select at least one employee job role.');
      return;
    }
    let role_targets;
    if (dialogTabScoped && editingKr?.id && activeRole) {
      const levels = levelsByRoleId[activeRole.id] || [];
      if (levels.length === 0) {
        toast.error(
          `Configure Employee Levels for “${activeRole.name}” under Administration → Employee Settings before setting OKR weights.`
        );
        return;
      }
      const sn = targetNumberByRoleLevel[activeRole.id] || {};
      const sp = targetPeriodByRoleLevel[activeRole.id] || {};
      const { n, p } = applyFrequencyToLevelDrafts(levels, sn, sp, formKeyResultFrequency);
      role_targets = mergeRoleTargetsForTabSave(
        editingKr,
        activeRole.id,
        levels,
        targetByRoleLevel[activeRole.id] || {},
        n,
        p
      );
    } else {
      role_targets = [];
      for (const r of selectedRoles) {
        const levels = levelsByRoleId[r.id] || [];
        if (levels.length === 0) {
          toast.error(
            `Configure Employee Levels for “${r.name}” under Administration → Employee Settings before setting OKR weights.`
          );
          return;
        }
        const byLvl = targetByRoleLevel[r.id] || {};
        const byNum = targetNumberByRoleLevel[r.id] || {};
        const byPer = targetPeriodByRoleLevel[r.id] || {};
        const { n, p } = applyFrequencyToLevelDrafts(levels, byNum, byPer, formKeyResultFrequency);
        levels.forEach((lvl) => {
          role_targets.push({
            employee_job_role_id: r.id,
            employee_title_id: lvl.id,
            target_percent: intPercent0to100(byLvl[lvl.id]),
            target_number: intWhole(n[lvl.id]),
            ...periodPayloadFromDraft(p[lvl.id])
          });
        });
      }
    }
    if (role_targets.length === 0) {
      toast.error('No employee level rows to save.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category_id: formCategoryId,
        name: String(formName).trim(),
        description: String(formDescription || '').trim() || null,
        key_result_frequency: formKeyResultFrequency,
        role_targets
      };
      if (!editingKr?.id) {
        payload.catalog_line_kind = 'custom';
      }
      if (editingKr?.id) {
        await api.put(`/okr/catalog-key-results/${editingKr.id}`, payload);
        toast.success('Key Result updated');
      } else {
        await api.post('/okr/catalog-key-results', payload);
        toast.success('Key Result created');
      }
      setDialogOpen(false);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  /** Removes targets for the active job role only; deletes the catalog row only if no targets remain anywhere. */
  const removeKrForActiveTab = async (kr) => {
    if (!activeRole) return;
    if (
      !window.confirm(
        `Remove “${kr.name}” (${kr.kr_code}) from the “${activeRole.name}” tab?\n\nThis only removes targets for this job role. Other tabs are unchanged. The catalog line and KR Id stay in the catalog when no job role has targets, so you can add it again later.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/okr/catalog-key-results/${kr.id}/employee-job-role/${activeRole.id}`);
      toast.success('Removed for this job role');
      if (selectedKrId === kr.id) {
        setSelectedKrId(null);
        setPanelDraft({});
        setPanelNumberDraft({});
        setPanelPeriodDraft({});
      }
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Remove failed');
    }
  };

  const closeTargetPanel = useCallback(() => {
    setSelectedKrId(null);
    setKeyResultTargetDialogOpen(false);
    setPanelDraft({});
    setPanelNumberDraft({});
    setPanelPeriodDraft({});
    setPanelFrequency({ ...DEFAULT_KEY_RESULT_FREQ });
  }, []);

  const openTargetPanel = useCallback((kr) => {
    setSelectedKrId(kr.id);
    setPanelFrequency(normalizeKeyResultFreq(kr.key_result_frequency));
    if (keyResultRowMode === 'popup') {
      setKeyResultTargetDialogOpen(true);
    } else {
      setKeyResultTargetDialogOpen(false);
    }
  }, [keyResultRowMode]);

  const savePanelTargets = async () => {
    const kr = keyResults.find((k) => k.id === selectedKrId);
    if (!kr || !activeRole || !canModify) return;
    const { n, p } = applyFrequencyToLevelDrafts(
      levelColumnsForTab,
      panelNumberDraft,
      panelPeriodDraft,
      panelFrequency
    );
    const role_targets = mergeRoleTargetsForTabSave(
      kr,
      activeRole.id,
      levelColumnsForTab,
      panelDraft,
      n,
      p
    );
    setPanelSaving(true);
    try {
      await api.put(`/okr/catalog-key-results/${kr.id}`, {
        category_id: kr.category_id || kr.category?.id,
        name: kr.name,
        description: kr.description ?? null,
        key_result_frequency: panelFrequency,
        role_targets
      });
      toast.success('Category weights and targets saved');
      await loadData();
      closeTargetPanel();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    } finally {
      setPanelSaving(false);
    }
  };

  const applyPanelFromDaily = useCallback(() => {
    const f = normalizeKeyResultFreq(panelFrequency);
    if (!f.daily || !f.weekly || !f.monthly || !f.quarterly || !f.yearly) return;
    setPanelPeriodDraft((prev) => {
      const next = { ...prev };
      for (const lvl of levelColumnsForTab) {
        const d = intWhole(panelNumberDraft[lvl.id] ?? '');
        if (!d) continue;
        const base = prev[lvl.id] || emptyPeriodDraft();
        next[lvl.id] = {
          ...base,
          weekly: String(FROM_DAILY_MULT.weekly * d),
          monthly: String(FROM_DAILY_MULT.monthly * d),
          quarterly: String(FROM_DAILY_MULT.quarterly * d),
          yearly: String(FROM_DAILY_MULT.yearly * d)
        };
      }
      return next;
    });
  }, [levelColumnsForTab, panelFrequency, panelNumberDraft, panelPeriodDraft]);

  const applyFormRoleFromDaily = useCallback(
    (role) => {
      const f = normalizeKeyResultFreq(formKeyResultFrequency);
      if (!f.daily || !f.weekly || !f.monthly || !f.quarterly || !f.yearly) return;
      const levels = levelsByRoleId[role.id] || [];
      setTargetPeriodByRoleLevel((prev) => {
        const per = { ...prev, [role.id]: { ...prev[role.id] } };
        const byNum = targetNumberByRoleLevel[role.id] || {};
        for (const lvl of levels) {
          const d = intWhole(byNum[lvl.id] ?? '');
          if (!d) continue;
          per[role.id] = per[role.id] || {};
          per[role.id][lvl.id] = {
            weekly: String(FROM_DAILY_MULT.weekly * d),
            monthly: String(FROM_DAILY_MULT.monthly * d),
            quarterly: String(FROM_DAILY_MULT.quarterly * d),
            yearly: String(FROM_DAILY_MULT.yearly * d)
          };
        }
        return per;
      });
    },
    [formKeyResultFrequency, levelsByRoleId, targetNumberByRoleLevel]
  );

  /** When employee levels load async, add empty cells for new level keys */
  useEffect(() => {
    setTargetByRoleLevel((prev) => {
      let changed = false;
      const next = { ...prev };
      selectedRoles.forEach((r) => {
        const levels = levelsByRoleId[r.id] || [];
        if (!next[r.id]) next[r.id] = {};
        levels.forEach((lvl) => {
          if (next[r.id][lvl.id] === undefined) {
            next[r.id][lvl.id] = '';
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
    setTargetNumberByRoleLevel((prev) => {
      let changed = false;
      const next = { ...prev };
      selectedRoles.forEach((r) => {
        const levels = levelsByRoleId[r.id] || [];
        if (!next[r.id]) next[r.id] = {};
        levels.forEach((lvl) => {
          if (next[r.id][lvl.id] === undefined) {
            next[r.id][lvl.id] = '';
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
    setTargetPeriodByRoleLevel((prev) => {
      let changed = false;
      const next = { ...prev };
      selectedRoles.forEach((r) => {
        const levels = levelsByRoleId[r.id] || [];
        if (!next[r.id]) next[r.id] = {};
        levels.forEach((lvl) => {
          if (next[r.id][lvl.id] === undefined) {
            next[r.id][lvl.id] = emptyPeriodDraft();
            changed = true;
          }
        });
      });
      return changed ? next : prev;
    });
  }, [levelsByRoleId, selectedRoles]);

  /** Each tab lists only catalog KRs that have at least one target row for this job role. */
  const keyResultsByPageFilter = useMemo(() => {
    if (!filterCategoryId) return keyResults;
    return keyResults.filter((kr) => String(kr.category_id) === String(filterCategoryId));
  }, [keyResults, filterCategoryId]);

  const filteredKeyResults = useMemo(() => {
    if (!activeRole?.id) return keyResultsByPageFilter;
    return keyResultsByPageFilter.filter((kr) => catalogRowHasTargetForJobRole(kr, activeRole.id));
  }, [keyResultsByPageFilter, keyResults, activeRole?.id]);

  const sortedKeyResults = useMemo(() => {
    const rows = [...filteredKeyResults];
    rows.sort((a, b) => compareCatalogRows(a, b, orderBy, order, { activeRoleId: activeRole?.id }));
    return rows;
  }, [filteredKeyResults, orderBy, order, activeRole?.id]);

  const noRowsInTabHint =
    keyResults.length > 0 && filteredKeyResults.length === 0 && Boolean(activeRole?.id);

  const selectedKr = useMemo(
    () => (selectedKrId ? keyResults.find((k) => String(k.id) === String(selectedKrId)) : null),
    [keyResults, selectedKrId]
  );

  useEffect(() => {
    if (!selectedKrId || !activeRole) {
      setPanelDraft({});
      setPanelNumberDraft({});
      setPanelPeriodDraft({});
      return;
    }
    const kr = keyResults.find((k) => String(k.id) === String(selectedKrId));
    if (!kr) return;
    const m = {};
    const mN = {};
    const mP = {};
    levelColumnsForTab.forEach((lvl) => {
      const p = pctForTargetLevel(kr, activeRole.id, lvl.id);
      const nv = numForTargetLevel(kr, activeRole.id, lvl.id);
      m[lvl.id] = p != null && p > 0 ? String(intPercent0to100(p)) : '';
      mN[lvl.id] = nv != null ? String(nv) : '';
      const t = (kr.targets || []).find(
        (x) =>
          String(targetRowJobRoleId(x) ?? '') === String(activeRole.id) &&
          String(targetRowTitleId(x) ?? '') === String(lvl.id)
      );
      mP[lvl.id] = t ? periodDraftFromTargetRow(t) : emptyPeriodDraft();
    });
    setPanelDraft(m);
    setPanelNumberDraft(mN);
    setPanelPeriodDraft(mP);
  }, [selectedKrId, activeRole?.id, keyResults, levelColumnsForTab]);

  useEffect(() => {
    setSelectedKrId(null);
    setPanelDraft({});
    setPanelNumberDraft({});
    setPanelPeriodDraft({});
  }, [roleTabIndex]);

  useEffect(() => {
    if (!selectedKrId) return;
    if (!filteredKeyResults.some((k) => String(k.id) === String(selectedKrId))) {
      setSelectedKrId(null);
      setPanelDraft({});
      setPanelNumberDraft({});
      setPanelPeriodDraft({});
    }
  }, [filteredKeyResults, selectedKrId]);

  useEffect(() => {
    if (!selectedKrId) return;
    const kr = keyResults.find((k) => String(k.id) === String(selectedKrId));
    if (kr) setPanelFrequency(normalizeKeyResultFreq(kr.key_result_frequency));
  }, [keyResults, selectedKrId]);

  useEffect(() => {
    if (!selectedKrId) return;
    setKeyResultTargetDialogOpen(keyResultRowMode === 'popup');
  }, [keyResultRowMode, selectedKrId]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%', mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          KR+Cat(%)+Target(Nos)
        </Typography>
        <Tooltip
          title={
            <Typography variant="caption">
              DWMQY = Daily, Weekly, Monthly, Quarterly, Yearly. Daily can roll up to W/M/Q/Y as a starting point.
            </Typography>
          }
          placement="right"
          arrow
        >
          <IconButton size="small" aria-label="About DWMQY in matrix">
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each <strong>tab</strong> is a job role (e.g. Bench Sales, Recruiter).         Set <strong>Cat in % / Daily, Weekly, Monthly, Quarterly, Yearly</strong> per
        employee level; category header rows show column totals
        for weights. Use{' '}
        <strong>Publish</strong> to record the current catalog snapshot for this job role (Revoke clears it). Deleting a row
        removes targets for <strong>this tab&apos;s role</strong> only. Use <strong>Create Key Result</strong> on a category
        row or <strong>+Add</strong> on the right to add a line; <strong>Actions → Edit</strong> opens the full multi–job-role
        editor. New KR IDs look like <strong>XXXX-SYST-0001-0000-0001</strong> (system) or{' '}
        <strong>XXXX-CUST-0001-0000-0001</strong> (custom).{' '}
        <strong>Weekly–yearly</strong> targets are not on this intro screen: they appear in the <strong>matrix</strong> after
        you scroll in Create/Edit, or in the <strong>side panel</strong> when you click a data row (not the category header).
      </Typography>
      {loading ? (
        <LinearProgress />
      ) : sortedRoles.length === 0 ? (
        <Typography color="text.secondary">No active employee job roles. Add them under Administration → Employee Settings.</Typography>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={sortedRoles.length ? Math.min(roleTabIndex, sortedRoles.length - 1) : 0}
              onChange={(_, v) => setRoleTabIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {sortedRoles.map((r) => (
                <Tab key={r.id} label={r.name} />
              ))}
            </Tabs>
          </Box>
          {activeRole && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                py: 1,
                px: 0.5
              }}
            >
              <Typography variant="body2" color="text.secondary" component="span">
                Published on:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {publishRecordForActiveRole?.published_at
                    ? new Date(publishRecordForActiveRole.published_at).toLocaleString()
                    : '—'}
                </Box>
              </Typography>
              <Button
                variant="contained"
                size="small"
                disabled={!canModify || publishBusy || !levelColumnsForTab.length}
                onClick={handlePublishCatalog}
              >
                {publishBusy ? 'Working…' : 'Publish'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                disabled={!canModify || publishBusy || !publishRecordForActiveRole}
                onClick={handleRevokePublish}
              >
                Revoke
              </Button>
            </Box>
          )}

          {activeRole && levelColumnsForTab.length > 0 && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mt: 0.5 }}>
              <Tabs
                value={definerSubTab}
                onChange={(_, v) => setDefinerSubTab(v)}
                variant="fullWidth"
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="1) Choose from template" />
                <Tab label="2) Set cat % & targets (matrix) — Current working set" />
              </Tabs>
            </Box>
          )}

          {activeRole && definerSubTab === 0 && levelColumnsForTab.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <KeyResultTemplatePanel
                categories={categoryOptions}
                isTemplateInWorkingSet={isTemplateRowAdded}
                onAddTemplate={(t) => addKeyResultFromTemplate(t)}
                onRemoveTemplate={removeTemplateFromWorking}
                onAddAllInCategory={(code) => handleAddAllTemplatesInCategory(null, code)}
                busyId={templateBusyId}
                canAdd={canAdd}
                canDelete={canDelete}
                inactiveTemplateIds={inactiveTemplateIds}
                onTemplateActiveChange={toggleTemplateLibraryActive}
                canModify={canModify}
                resolveCatalogKeyResultForTemplate={getCatalogRowForTemplate}
              />
              <KeyResultCustomCatalogPanel
                categories={categoryOptions}
                rows={customCatalogRows}
                isInWorkingSet={isCustomRowInWorkingSet}
                onAdd={addCustomCatalogToWorking}
                onRemove={removeCustomCatalogFromWorking}
                onToggleActive={toggleCustomCatalogActive}
                onCreateCustom={() => openCreateForTab(null)}
                busyId={templateBusyId}
                canAdd={canAdd}
                canDelete={canDelete}
                canModify={canModify}
              />
            </Box>
          )}

          {activeRole && levelColumnsForTab.length === 0 && (
            <Typography color="warning.main" variant="body2" sx={{ mb: 1.5 }}>
              No Employee Levels for &quot;{activeRole.name}&quot;. Define them under Administration → Employee Settings →
              Employee Levels.
            </Typography>
          )}

          {definerSubTab === 1 && (
            <>
              <Card sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 260 }} size="small">
                    <InputLabel>Filter by category (matrix only)</InputLabel>
                    <Select
                      label="Filter by category (matrix only)"
                      value={filterCategoryId}
                      onChange={(e) => setFilterCategoryId(e.target.value)}
                    >
                      <MenuItem value="">All categories</MenuItem>
                      {categoryOptions.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c.theme_color || '#6366f1' }} />
                            {c.name}
                            {c.is_active === false ? ' (disabled)' : ''}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <FormLabel
                    id="kr-row-mode-label"
                    sx={{ display: 'block', fontSize: '0.95rem', fontWeight: 700, mb: 0.5 }}
                  >
                    When you click a Key Result row
                  </FormLabel>
                  <RadioGroup
                    row={false}
                    aria-labelledby="kr-row-mode-label"
                    name="keyResultRowMode"
                    value={keyResultRowMode}
                    onChange={(_, v) => {
                      setKeyResultRowMode(v);
                      try {
                        localStorage.setItem(LS_KR_ROW_MODE, v);
                      } catch {
                        /* ignore */
                      }
                    }}
                    sx={{ gap: 0.5 }}
                  >
                    <FormControlLabel
                      value="side"
                      control={<Radio size="small" />}
                      label={
                        <span>
                          <strong>Side sheet</strong> — the matrix is hidden; the editor opens on the right (or below on small
                          screens). After Save or Close, the full matrix is visible again.
                        </span>
                      }
                    />
                    <FormControlLabel
                      value="popup"
                      control={<Radio size="small" />}
                      label={
                        <span>
                          <strong>Popup</strong> — a wide window opens so all columns (Daily, Weekly, …) are easy to read; the
                          main matrix stays in the background.
                        </span>
                      }
                    />
                  </RadioGroup>
                </CardContent>
              </Card>
            </>
          )}

          {definerSubTab === 1 && (
            <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.45 }}>
            Click a <strong>catalog Key Result row</strong> (not the colored category header) to open the target editor. Choose{' '}
            <strong>Side sheet</strong> or <strong>Popup</strong> above. The matrix can hide while you edit; scroll
            horizontally when the matrix is visible; drag column borders in the header to resize.
          </Typography>
          {canView &&
            activeRole &&
            levelColumnsForTab.length > 0 &&
            filteredKeyResults.length > 0 &&
            !selectedKrId && (
              <Alert severity="info" sx={{ mb: 1.5 }} variant="outlined">
                Tip: click any numbered Key Result row to open the side panel with the weekly–yearly columns. Those columns
                also appear in the Create / Edit Key Result dialog—scroll past name and description to the matrix table.
              </Alert>
            )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              alignItems: 'flex-start',
              width: '100%'
            }}
          >
            <Box
              sx={{
                flex: selectedKrId && keyResultRowMode === 'side' ? 0 : 1,
                minWidth: 0,
                width: '100%',
                display: selectedKrId && keyResultRowMode === 'side' ? 'none' : 'block',
                maxWidth: '100%'
              }}
            >
              <CatalogMatrixTable
                sortedKeyResults={sortedKeyResults}
                levelColumns={levelColumnsForTab}
                activeRoleId={activeRole?.id}
                hasAnyRows={filteredKeyResults.length > 0}
                noRowsInTabHint={noRowsInTabHint}
                orderBy={orderBy}
                order={order}
                onRequestSort={handleRequestSort}
                showRowActions={showRowActions}
                canView={canView}
                canModify={canModify}
                canDelete={canDelete}
                onView={openView}
                onEdit={openEditFull}
                onRemove={removeKrForActiveTab}
                onRowClick={canView && activeRole && levelColumnsForTab.length > 0 ? openTargetPanel : undefined}
                selectedKrId={selectedKrId}
                showAddColumn={Boolean(canAdd && activeRole && levelColumnsForTab.length > 0)}
                onAddRow={openCreateForTab}
                canAdd={canAdd}
                columnWidths={columnWidths}
                setColumnWidths={setColumnWidths}
                scrollFrameSx={{
                  height: { xs: 'calc(100dvh - 380px)', sm: 'calc(100dvh - 340px)' },
                  maxHeight: 'min(65dvh, calc(100dvh - 220px))'
                }}
              />
            </Box>
            {selectedKr && activeRole && levelColumnsForTab.length > 0 && keyResultRowMode === 'side' && (
              <Paper
                elevation={2}
                sx={{
                  width: { xs: '100%', lg: 'min(1200px, 100%)' },
                  maxWidth: '100%',
                  flex: { lg: '1 1 0' },
                  flexShrink: 0,
                  p: 2,
                  position: { lg: 'sticky' },
                  top: { lg: 16 },
                  alignSelf: 'flex-start',
                  maxHeight: { lg: 'min(82vh, calc(100dvh - 120px))' },
                  overflow: 'auto',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  mx: { lg: 'auto' }
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, pr: 1 }}>
                    Category weight and targets — {activeRole.name}
                  </Typography>
                  <IconButton size="small" onClick={closeTargetPanel} aria-label="Close panel" type="button">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Chip
                  size="small"
                  label={selectedKr.kr_code}
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', mb: 1 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {selectedKr.name}
                </Typography>
                {selectedKr.description ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1.45 }}>
                    {selectedKr.description}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {selectedKr.category?.name || '—'}
                  {selectedKr.category?.category_code ? ` · ${selectedKr.category.category_code}` : ''}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Per employee level: <strong>Daily</strong> is the main count target; W/M/Q/Y are optional by frequency
                  below.
                </Typography>
                <TargetsEntryTable
                  levels={levelColumnsForTab}
                  disabled={!canModify || panelSaving}
                  weightByLevel={panelDraft}
                  onWeightChange={(lvlId, v) =>
                    setPanelDraft((prev) => ({ ...prev, [lvlId]: v }))
                  }
                  numberByLevel={panelNumberDraft}
                  onNumberChange={(lvlId, v) =>
                    setPanelNumberDraft((prev) => ({ ...prev, [lvlId]: v }))
                  }
                  periodByLevel={panelPeriodDraft}
                  onPeriodChange={(lvlId, pk, v) =>
                    setPanelPeriodDraft((prev) => ({
                      ...prev,
                      [lvlId]: { ...(prev[lvlId] || emptyPeriodDraft()), [pk]: v }
                    }))
                  }
                  frequency={panelFrequency}
                  onFrequencyChange={canModify && !panelSaving ? (nf) => setPanelFrequency(nf) : null}
                  readOnlyFrequency={!canModify}
                  onApplyFromDaily={canModify && !panelSaving ? applyPanelFromDaily : null}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  {canModify && (
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={savePanelTargets}
                      disabled={panelSaving}
                      size="small"
                    >
                      {panelSaving ? 'Saving…' : 'Save'}
                    </Button>
                  )}
                  <Button variant="outlined" onClick={closeTargetPanel} size="small" type="button">
                    Close
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
          <Dialog
            open={Boolean(selectedKr && keyResultTargetDialogOpen && activeRole && levelColumnsForTab.length)}
            onClose={closeTargetPanel}
            maxWidth={false}
            fullWidth
            scroll="body"
            PaperProps={{
              sx: {
                width: 'min(1900px, 97vw)',
                maxWidth: '100%',
                m: 1.5,
                minHeight: '50vh',
                maxHeight: 'min(90vh, 960px)',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 1,
                flexWrap: 'wrap',
                pr: 6
              }}
            >
              <Box>
                <Typography component="div" variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Key Result targets — {activeRole?.name}
                </Typography>
                {selectedKr && (
                  <>
                    <Chip size="small" label={selectedKr.kr_code} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {selectedKr.name}
                    </Typography>
                    {selectedKr.description ? (
                      <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5, maxWidth: 720 }}>
                        {selectedKr.description}
                      </Typography>
                    ) : null}
                  </>
                )}
              </Box>
              <IconButton aria-label="Close" onClick={closeTargetPanel} type="button" size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto'
              }}
            >
              {selectedKr && activeRole && levelColumnsForTab.length > 0 && (
                <>
                  <TargetsEntryTable
                    levels={levelColumnsForTab}
                    disabled={!canModify || panelSaving}
                    weightByLevel={panelDraft}
                    onWeightChange={(lvlId, v) => setPanelDraft((prev) => ({ ...prev, [lvlId]: v }))}
                    numberByLevel={panelNumberDraft}
                    onNumberChange={(lvlId, v) =>
                      setPanelNumberDraft((prev) => ({ ...prev, [lvlId]: v }))
                    }
                    periodByLevel={panelPeriodDraft}
                    onPeriodChange={(lvlId, pk, v) =>
                      setPanelPeriodDraft((prev) => ({
                        ...prev,
                        [lvlId]: { ...(prev[lvlId] || emptyPeriodDraft()), [pk]: v }
                      }))
                    }
                    frequency={panelFrequency}
                    onFrequencyChange={canModify && !panelSaving ? (nf) => setPanelFrequency(nf) : null}
                    readOnlyFrequency={!canModify}
                    onApplyFromDaily={canModify && !panelSaving ? applyPanelFromDaily : null}
                  />
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {canModify && (
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={savePanelTargets}
                        disabled={panelSaving}
                        size="small"
                        type="button"
                      >
                        {panelSaving ? 'Saving…' : 'Save'}
                      </Button>
                    )}
                    <Button variant="outlined" onClick={closeTargetPanel} size="small" type="button">
                      Close
                    </Button>
                  </Box>
                </>
              )}
            </DialogContent>
          </Dialog>
          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              flexWrap: 'nowrap',
              px: 1,
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 0.5
            }}
          >
            <Box
              component="span"
              role="img"
              aria-hidden
              sx={{
                fontSize: '1.85rem',
                lineHeight: 1,
                cursor: 'default',
                animation: `${handPointPulse} 1.15s ease-in-out infinite`,
                userSelect: 'none'
              }}
            >
              👈
            </Box>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => setMatrixDialogOpen(true)}
              sx={{
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'primary.main',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              OKR Role Matrix — KR Applicability & Weight by Level
            </Link>
            <Box
              component="span"
              role="img"
              aria-hidden
              sx={{
                fontSize: '1.85rem',
                lineHeight: 1,
                cursor: 'default',
                animation: `${handPointPulse} 1.15s ease-in-out infinite`,
                animationDelay: '0.2s',
                userSelect: 'none'
              }}
            >
              👉
            </Box>
          </Box>
          <Dialog
            open={matrixDialogOpen}
            onClose={() => setMatrixDialogOpen(false)}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                width: '98vw',
                maxWidth: 2000,
                height: '90vh',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
          >
            <DialogTitle
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.35,
                pr: 6,
                flexShrink: 0
              }}
            >
              OKR Role Matrix — KR Applicability & Weight by Level
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                pt: 1,
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5, flexShrink: 0 }}
              >
                Pick a job role tab, then review Cat in % / Target (#) and weekly–yearly targets per Employee Level column.
                Drag header edges to resize; scroll to pan.
              </Typography>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, flexShrink: 0 }}>
                <Tabs
                  value={sortedRoles.length ? Math.min(roleTabIndex, sortedRoles.length - 1) : 0}
                  onChange={(_, v) => setRoleTabIndex(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  {sortedRoles.map((r) => (
                    <Tab key={r.id} label={r.name} />
                  ))}
                </Tabs>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <CatalogMatrixTable
                  sortedKeyResults={sortedKeyResults}
                  levelColumns={levelColumnsForTab}
                  activeRoleId={activeRole?.id}
                  hasAnyRows={filteredKeyResults.length > 0}
                  noRowsInTabHint={noRowsInTabHint}
                  orderBy={orderBy}
                  order={order}
                  onRequestSort={handleRequestSort}
                  showRowActions={false}
                  canView={false}
                  canModify={false}
                  canDelete={false}
                  onView={() => {}}
                  onEdit={() => {}}
                  onRemove={() => {}}
                  columnWidths={columnWidths}
                  setColumnWidths={setColumnWidths}
                  scrollFrameSx={{
                    height: '100%',
                    minHeight: 260,
                    maxHeight: '100%'
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ flexShrink: 0 }}>
              <Button variant="contained" onClick={() => setMatrixDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
            </>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'view'
            ? 'View Key Result'
            : dialogTabScoped && activeRole
              ? `${editingKr ? 'Edit' : 'Create'} Key Result — ${activeRole.name}`
              : editingKr
                ? 'Edit Key Result'
                : 'Create Key Result'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'view' && editingKr?.kr_code && (
            <Box sx={{ mb: 1, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Key Result ID
              </Typography>
              <Chip size="small" label={editingKr.kr_code} variant="outlined" sx={{ fontFamily: 'monospace' }} />
            </Box>
          )}
          <FormControl fullWidth margin="normal" required disabled={dialogMode === 'view'}>
            <InputLabel>Key Result Category</InputLabel>
            <Select
              label="Key Result Category"
              value={formCategoryId}
              onChange={(e) => setFormCategoryId(e.target.value)}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: c.theme_color || '#6366f1' }} />
                    {c.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Key Result name"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
            disabled={dialogMode === 'view'}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Key Result description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            disabled={dialogMode === 'view'}
            multiline
            minRows={2}
            placeholder="Optional longer explanation of this catalog line"
          />
          {dialogTabScoped && activeRole ? (
            <Box sx={{ mt: 2, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Employee job role (this tab)
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {activeRole.name}
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              multiple
              sx={{ mt: 2 }}
              options={roles}
              getOptionLabel={(o) => o.name || ''}
              value={selectedRoles}
              onChange={onRolesChange}
              disabled={dialogMode === 'view'}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Employee job roles" placeholder="Select one or more" required />
              )}
            />
          )}
          {selectedRoles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Cat in %, Daily, Weekly, Monthly, Quarterly, Yearly — by employee level
                {dialogTabScoped && activeRole ? ` — ${activeRole.name}` : ''}
              </Typography>
              {(dialogTabScoped && activeRole ? [activeRole] : selectedRoles).map((role, roleIdx) => {
                const levels = [...(levelsByRoleId[role.id] || [])].sort(
                  (a, b) =>
                    Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
                    String(a.name || '').localeCompare(String(b.name || ''))
                );
                return (
                  <Box key={role.id} sx={{ mb: 2, pl: 0.5, borderLeft: 3, borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                      {role.name}
                    </Typography>
                    {levels.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No Employee Levels for this role. Configure under Administration → Employee Settings → Employee
                        Levels.
                      </Typography>
                    ) : (
                      <TargetsEntryTable
                        levels={levels}
                        disabled={dialogMode === 'view'}
                        weightByLevel={targetByRoleLevel[role.id] || {}}
                        onWeightChange={(lvlId, v) =>
                          setTargetByRoleLevel((prev) => ({
                            ...prev,
                            [role.id]: { ...prev[role.id], [lvlId]: v }
                          }))
                        }
                        numberByLevel={targetNumberByRoleLevel[role.id] || {}}
                        onNumberChange={(lvlId, v) =>
                          setTargetNumberByRoleLevel((prev) => ({
                            ...prev,
                            [role.id]: { ...prev[role.id], [lvlId]: v }
                          }))
                        }
                        periodByLevel={targetPeriodByRoleLevel[role.id] || {}}
                        onPeriodChange={(lvlId, pk, v) =>
                          setTargetPeriodByRoleLevel((prev) => ({
                            ...prev,
                            [role.id]: {
                              ...(prev[role.id] || {}),
                              [lvlId]: {
                                ...(prev[role.id]?.[lvlId] || emptyPeriodDraft()),
                                [pk]: v
                              }
                            }
                          }))
                        }
                        frequency={formKeyResultFrequency}
                        onFrequencyChange={
                          dialogMode === 'view' || roleIdx > 0
                            ? null
                            : (nf) => setFormKeyResultFrequency(nf)
                        }
                        readOnlyFrequency={dialogMode === 'view'}
                        onApplyFromDaily={
                          dialogMode === 'view' || roleIdx > 0
                            ? null
                            : () => applyFormRoleFromDaily(role)
                        }
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{dialogMode === 'view' ? 'Close' : 'Cancel'}</Button>
          {dialogMode !== 'view' && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveKr} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
